import { Ref, useCallback, useImperativeHandle, useState } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import {
  isLoading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  AttachArticles,
  ItemWithQuantity,
} from "../../../AttachArticles/AttachArticles";
import {
  ItemNoAndType,
  ItemNoAndTypeWithQuantity,
} from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { useAllAttachedItems } from "@coworker/apprestructured/src/task-management/services/useAllAttachedItems";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import {
  shouldShowField,
  VisibilityCondition,
} from "@coworker/apprestructured/src/task-management/helpers/visibilityCondition.helper";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { FixaToast } from "@coworker/apprestructured/src/shared/wrappers/FixaToast/FixaToast";
import {
  AddArticlesFragment,
  type AddArticlesSelection,
} from "@coworker/apprestructured/src/task-management/components/AddArticlesFragment/AddArticlesFragment";
import { useGetItemOrLocalCombination } from "@coworker/apprestructured/src/shared/hooks/item/useGetItemOrLocalCombination";
import { useTranslation } from "@coworker/locales";

export type AttachArticlesCreatorFragmentConfigProps = {
  field: string;
  maxArticles: number;
  minItems?: number;
  visibility?: VisibilityCondition;
};

type AttachArticlesCreatorFragmentProps = {
  configProps: AttachArticlesCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function AttachArticlesCreatorFragment({
  configProps,
  commonProps,
  ref,
}: AttachArticlesCreatorFragmentProps) {
  const { field, maxArticles, minItems = 0, visibility } = configProps;
  const { blockInput, task, onTaskChange, taskType, showValidation } =
    commonProps;
  const [showAddArticlesFragment, setShowAddArticlesFragment] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const { t } = useTranslation();
  const getItemOrLocalCombination = useGetItemOrLocalCombination();

  const isCustomTask =
    !isLoading(taskType) && taskType.name === TaskTypeNames.CUSTOM_TASK;
  const isCustomTestbuyArticlesField = field.endsWith("custom_articles");

  const rawItemNosAndType = validateInitialDetailsFieldValue<ItemNoAndType[]>(
    getByPath(task, field),
    field,
    taskType
  );

  // Normalise: add quantity=1 for items that predate the quantity field (backward compat)
  // Also handles backend returning quantity as a string (e.g. "1")
  const itemNosAndTypeWithQuantity = isLoading(rawItemNosAndType)
    ? LOADING
    : (rawItemNosAndType ?? []).map((item) => {
        const existingQty = (item as Partial<ItemNoAndTypeWithQuantity>)
          .quantity;
        const parsed =
          typeof existingQty === "string"
            ? parseInt(existingQty, 10)
            : existingQty;
        const normalized =
          typeof parsed === "number" && !isNaN(parsed) ? parsed : 1;
        const quantity = Math.max(1, normalized);
        return { ...item, quantity } as ItemNoAndTypeWithQuantity;
      });

  const { items, isFetchingItems } = useAllAttachedItems(
    isLoading(itemNosAndTypeWithQuantity) ? LOADING : itemNosAndTypeWithQuantity
  );

  const itemsWithQuantity: ItemWithQuantity[] = isLoading(
    itemNosAndTypeWithQuantity
  )
    ? []
    : items.map((item) => {
        const match = itemNosAndTypeWithQuantity.find((i) => i.no === item.no);
        return { ...item, quantity: match?.quantity ?? 1 };
      });

  const currentArticles = isLoading(itemNosAndTypeWithQuantity)
    ? []
    : itemNosAndTypeWithQuantity;

  const handleConfirmClick = useCallback(
    (newItemNosAndType: ItemNoAndTypeWithQuantity[]) => {
      // Strip quantity from the payload for non-custom task types
      const payload = isCustomTask
        ? newItemNosAndType
        : newItemNosAndType.map(({ no, type }) => ({ no, type }));
      onTaskChange(setByPath(field, payload));
    },
    [field, onTaskChange, isCustomTask]
  );

  const handleCustomTestbuySubmit = useCallback(
    async (selection: AddArticlesSelection) => {
      const existingNos = new Set(currentArticles.map(({ no }) => no));
      const requestedItems =
        selection.mode === "attach"
          ? selection.articles.map(({ item }) => ({
              no: item.no,
              type: item.type,
            }))
          : await Promise.all(
              selection.articles.map(async ({ productArticleId }) => {
                try {
                  const item =
                    await getItemOrLocalCombination(productArticleId);
                  return item ? { no: item.no, type: item.type } : null;
                } catch {
                  return null;
                }
              })
            );

      const additions: ItemNoAndType[] = [];
      let hasDuplicate = false;
      let hasMissingItem = false;
      for (const item of requestedItems) {
        if (!item) {
          hasMissingItem = true;
          continue;
        }
        if (existingNos.has(item.no)) {
          hasDuplicate = true;
          continue;
        }
        existingNos.add(item.no);
        additions.push(item);
      }

      if (additions.length > 0) {
        onTaskChange(
          setByPath(field, [
            ...currentArticles.map(({ no, type }) => ({ no, type })),
            ...additions,
          ])
        );
      }

      if (hasDuplicate) {
        setToastMessage("articleAlreadyExistsString");
      } else if (hasMissingItem) {
        setToastMessage("couldNotAddArticleString");
      }

      setShowAddArticlesFragment(false);
    },
    [currentArticles, field, getItemOrLocalCombination, onTaskChange]
  );

  const isVisible =
    !visibility || isLoading(task) || shouldShowField(task, visibility);

  const valid =
    !isVisible ||
    (!isLoading(itemNosAndTypeWithQuantity) &&
      itemNosAndTypeWithQuantity.length >= minItems);

  useImperativeHandle(ref, () => ({ valid }), [valid]);

  if (!isVisible) {
    return null;
  }

  if (isLoading(rawItemNosAndType)) {
    return <LoadingCreatorFragment />;
  }

  const showError = showValidation && !valid && minItems > 0;

  return (
    <>
      <AttachArticles
        items={itemsWithQuantity}
        setItemNosAndType={handleConfirmClick}
        disabled={blockInput || isFetchingItems}
        maxArticles={maxArticles}
        showQuantity={isCustomTask}
        showError={showError}
        minItems={minItems}
        onAddArticlesClick={
          isCustomTestbuyArticlesField
            ? () => setShowAddArticlesFragment(true)
            : undefined
        }
      />
      {isCustomTestbuyArticlesField && showAddArticlesFragment && (
        <AddArticlesFragment
          onClose={() => setShowAddArticlesFragment(false)}
          onSubmit={handleCustomTestbuySubmit}
          preselectedItemNos={currentArticles.map(({ no }) => no)}
          maxArticles={Math.max(0, maxArticles - currentArticles.length)}
          articleNumberOnly
        />
      )}
      <FixaToast
        isOpen={!!toastMessage}
        text={toastMessage ? t(toastMessage) : ""}
        onCloseRequest={() => setToastMessage("")}
      />
    </>
  );
}

export default AttachArticlesCreatorFragment;
